import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "../ui/dialog";
import { DollarSign, Send } from "lucide-react"; // Added Send icon for publish
import { Button } from "../ui/button"; // Added Button import

const normalizeStatus = (status) => {
  const statusMap = {
    'available': 'Available',
    'in_use': 'In Use',
    'maintenance': 'Maintenance',
  };
  return statusMap[status] || 'Available';
};

const StatusBadge = ({ status }) => {
  const normalizedStatus = normalizeStatus(status);

  const styles = {
    Available: 'text-muted-foreground bg-green-50 border-green-100 dark:bg-green-900/20 dark:border-green-900/30 dark:text-green-400',
    'In Use': 'text-orange-600 bg-orange-50 border-orange-100 dark:bg-orange-900/20 dark:border-orange-900/30 dark:text-orange-400',
    Maintenance: 'text-red-600 bg-red-50 border-red-100 dark:bg-red-900/20 dark:border-red-900/30 dark:text-red-400',
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[normalizedStatus] || styles.Available}`}>
      {normalizedStatus === 'Available' && '○ Available'}
      {normalizedStatus === 'In Use' && '○ In Use'}
      {normalizedStatus === 'Maintenance' && '⚡ Maintenance'}
    </span>
  );
};

export default function EquipmentDetailsModal({ open, onOpenChange, equipment, onPublish }) {
  if (!equipment) return null;

  const isDraft = equipment?.is_draft === 1; // Check if the item is a draft
  const name = equipment?.equipment_name || equipment.name || 'Unknown Equipment';
  const description = equipment?.description || 'N/A';
  const availability = equipment?.in_use ? 'Busy' : 'Available';
  const normalizedStatus = normalizeStatus(equipment.status);
  const itemModel = equipment.model_number || equipment.equipment_type || 'N/A';
  const itemCategory = equipment.category || 'General';
  const itemPrice = equipment.daily_rental_rate ? `${equipment.daily_rental_rate}/day` : 'N/A';
  const lastMaintenance = equipment?.last_maintenance_date || equipment.lastMaintenance || 'N/A';
  const nextMaintenance = equipment?.next_maintenance_due || equipment.nextMaintenance || 'N/A';
  const assignedTo = equipment?.assigned_to || "Unassigned";
  const listedAccessories = equipment?.equipment_accessories || [];
  const listedSpecs = equipment?.equipment_specs || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="fixed top-0 right-0 h-full w-full md:w-[640px] max-w-none rounded-none p-0 overflow-y-auto right-drawer-override"
      >
        <div className={`p-6 space-y-6 ${isDraft ? 'pb-24' : ''}`}>
          {/* Header */}
          <DialogHeader>
            <DialogTitle >
              <StatusBadge status={equipment.status} />
              <div className="flex items-center gap-3 mt-4">
                <div className="w-10 h-10 bg-muted flex items-center justify-center rounded-full font-medium">
                  {name.substring(0, 2).toUpperCase()}
                </div>

                <div className="flex flex-col">
                  <span className="text-lg font-semibold">{name}</span>
                  <span className="text-sm text-muted-foreground">
                    {itemModel}
                  </span>
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>

          {/* Equipment Information */}
          <section className="border rounded-xl p-4 space-y-4">
            <h3 className="font-semibold text-black-700">Equipment Information</h3>

            <div className="flex flex-col gap-4 text-sm">

              <div>
                <p className="font-medium">Description</p>
                <p className="text-muted-foreground text-sm">
                  {description}
                </p>
              </div>

              <div>
                <p className="font-medium">Assign to Crew Member</p>
                <p className="text-muted-foreground">{assignedTo}</p>
              </div>

              <div>
                <p className="font-medium">Last maintenance</p>
                <p className="text-muted-foreground">{lastMaintenance}</p>
              </div>

              <div>
                <p className="font-medium">Next maintenance due</p>
                <p className="text-muted-foreground">{nextMaintenance}</p>
              </div>

              {/* <div>
                <p className="font-medium">Pick-up Location</p>
                <p className="text-muted-foreground">
                  {equipment.pickupLocation}
                </p>
              </div>

              <div>
                <p className="font-medium">Check-out Date</p>
                <p className="text-muted-foreground">Select date</p>
              </div>

              <div>
                <p className="font-medium">Expected Return Date</p>
                <p className="text-muted-foreground">Select date</p>
              </div>

              <div>
                <p className="font-medium">Notes & Instructions</p>
                <p className="text-muted-foreground">
                  Any special instructions for using this equipment...
                </p>
              </div> */}
            </div>
          </section>

          {/* Included Accessories */}
          <section className="border rounded-xl p-4">
            <h3 className="font-semibold mb-3">Included Accessories</h3>
            {listedAccessories.length > 0 ? (
              <ul className="space-y-2 text-sm text-muted-foreground">
                {listedAccessories.map((item) => (
                  <li key={item.accessory_id}>• {item.accessory_name}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No accessories listed.</p>
            )}
          </section>

          {/* Listed Specs */}
          <section className="border rounded-xl p-4">
            <h3 className="font-semibold mb-3">Listed Specifications</h3>
            {listedSpecs.length > 0 ? (
              <ul className="space-y-2 text-sm text-muted-foreground">
                {listedSpecs.map((item) => (
                  <li key={item.spec_id}>• {item.spec_name} : {item.spec_value} </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No accessories listed.</p>
            )}
          </section>

          {/* Rental Duration */}
          <section className="border rounded-xl p-4">
            <h3 className="font-semibold">Rental Duration</h3>

            <div className="mt-3 space-y-3 text-sm">

              {/* <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Duration
                </span>
                <span>{equipment.duration}</span>
              </div> */}

              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Daily Rate
                </span>
                <span>${itemPrice}</span>
              </div>

              {/* <div className="flex items-center justify-between font-medium border-t pt-3">
                <span>Total Cost</span>
                <span className="text-blue-600">Select type</span>
              </div> */}
            </div>
          </section>

          {/* Usage History */}
          {/* <section>
            <h3 className="font-semibold mb-3">Usage History</h3>
            <div className="space-y-3">
              {equipment.usageHistory?.map((item, i) => (
                <div
                  key={i}
                  className="border rounded-xl p-3 flex flex-col gap-1 bg-white"
                >
                  <p className="font-medium">{item.title}</p>
                  <p className="text-sm text-muted-foreground">
                    Used by: {item.usedBy}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {item.dateRange}
                  </p>
                  <Badge className="bg-green-100 text-green-700 mt-1">
                    Available
                  </Badge>
                </div>
              ))}
            </div>
          </section> */}

          {/* Maintenance History */}
          {/* <section>
            <h3 className="font-semibold mb-3">Maintenance History</h3>
            <div className="space-y-3">
              {equipment.maintenance?.map((item, i) => (
                <div
                  key={i}
                  className="border rounded-xl p-3 bg-white flex justify-between"
                >
                  <div>
                    <p className="font-medium">{item.title}</p>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                    <p className="text-xs text-muted-foreground">{item.date}</p>
                  </div>
                  <span className="font-medium text-sm">{item.cost}</span>
                </div>
              ))}
            </div>
          </section> */}
        </div>

        {/* Publish Action Footer (Only visible if item is a draft) */}
        {isDraft && (
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-background border-t flex gap-3">
            <Button
              variant="outline"
              className="flex-1 rounded-lg"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-button-coloured gap-2 rounded-lg"
              onClick={onPublish}
            >
              <Send className="w-4 h-4" />
              Publish Equipment
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
